<template>
  <div v-if="email !== appStore.user.email" class="button-group">
    <Button v-if="userEntityId" icon="pi pi-times" label="Remove" :aria-label="deleteLabel" outlined rounded severity="warning" @click="toggleDialog('deleteQuestion' + props.userEntityId);"/>
    <div v-if="userId">
      <Button v-if="canLogin" icon="pi pi-trash" label="Block" :aria-label="banLabel" outlined rounded severity="danger" @click="toggleDialog('banQuestion' + props.userId);"/>
      <Button v-else icon="pi pi-thumbs-up" label="Unblock" :aria-label="unbanLabel" outlined rounded severity="danger" @click="toggleDialog('unbanQuestion' + props.userId);"/>
    </div>
  </div>
  <Dialog v-if="userEntityId" v-model:visible="isDeleteQuestionOpen" modal header="Delete User-Entity Association" :closable="false" :style="{ width: '30rem' }">
    <div class="row mb-8">
      <div class="col-2">
        <i :class="[ICONS.ExclamationTriangle, 'color-danger', 'fs-1']"></i>
      </div>
      <div class="col-10">
        <p>
          Are you sure you like to {{ deleteLabel }}?<br/>This will not prevent the user from adding this association again and will not affect any previously uploaded documents by this user.
        </p>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-content-end">
        <Button type="button" class="me-2" rounded raised @click="closeDialog();" label="Cancel" severity="secondary" />
        <Button type="button" rounded raised autofocus @click="closeDialog(); onDelete();" label="Yes" severity="danger" />
      </div>
    </template>
  </Dialog>
  <Dialog v-if="userId" v-model:visible="isBanQuestionOpen" modal header="Ban A User" :closable="false" :style="{ width: '30rem' }">
    <div class="row mb-8">
      <div class="col-2">
        <i :class="[ICONS.ExclamationTriangle, 'color-danger', 'fs-1']"></i>
      </div>
      <div class="col-10">
        <p>
          Are you sure you like to {{ banLabel }}?<br/>This user will no longer have access to the application unless administratively unbanned.
        </p>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-content-end">
        <Button type="button" class="me-2" rounded raised @click="closeDialog();" label="Cancel" severity="secondary" />
        <Button type="button" rounded raised autofocus @click="closeDialog(); onBan();" label="Yes" severity="danger" />
      </div>
    </template>
  </Dialog>
  <Dialog v-if="userId" v-model:visible="isUnbanQuestionOpen" modal header="Unban A User" :closable="false" :style="{ width: '30rem' }">
    <div class="row mb-8">
      <div class="col-2">
        <i :class="[ICONS.ExclamationTriangle, 'color-danger', 'fs-1']"></i>
      </div>
      <div class="col-10">
        <p>
          Are you sure you like to {{ unbanLabel }}?<br/>This user will now have access to use the application.
        </p>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-content-end">
        <Button type="button" class="me-2" rounded raised @click="closeDialog();" label="Cancel" severity="secondary" />
        <Button type="button" rounded raised autofocus @click="closeDialog(); onUnban();" label="Yes" severity="danger" />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import {computed} from 'vue';
import Button from "primevue/button";
import {ICONS, TOAST_SAVE_LIFE} from "@/utils/constants.js";
import Dialog from "primevue/dialog";
import {deleteAdminUserEntity, setAdminCanLogin} from "@/services/adminService.js";
import {useToast} from "primevue/usetoast";
import {useDialogStore} from "@/stores/dialogStore.js";
import {useAppStore} from "@/stores/appStore.js";

const toast = useToast();
const dialogStore = useDialogStore();
const appStore = useAppStore();

const props = defineProps({
  userId: {
    type: Number,
  },
  canLogin: {
    type: Boolean,
  },
  email: {
    type: String,
  },
  fullName: {
    type: String,
  },
  userEntityId: {
    type: Number,
    required: true,
  },
  entityName: {
    type: String,
    required: true,
  }
});

const emit = defineEmits(['modified']);

// Dialog Controls
const toggleDialog = (name) => dialogStore.toggle(name);
const closeDialog = () => dialogStore.close();
const isDeleteQuestionOpen = computed(() => dialogStore.isOpen('deleteQuestion' + props.userEntityId));
const isBanQuestionOpen = computed(() => dialogStore.isOpen('banQuestion' + props.userId));
const isUnbanQuestionOpen = computed(() => dialogStore.isOpen('unbanQuestion' + props.userId));

const onDelete = async () => {
  try {
    const result = await deleteAdminUserEntity(props.userEntityId);
    if (result) {
      toast.add({
        group: 'top-toast',
        severity: 'success',
        summary: 'Successfully Removed Association',
        detail: `${props.email} has been unassociated with ${props.entityName}.`,
        life: TOAST_SAVE_LIFE
      });
      emit('modified');
    } else {
      toast.add({
        group: 'top-toast',
        severity: 'error',
        summary: 'Error Removing Association',
        detail: `Error removing association between ${props.email} (${props.fullName}) and ${props.entityName}).\nInvalid query result.`,
        life: TOAST_SAVE_LIFE
      });
    }
  } catch (e) {
    toast.add({
      group: 'top-toast',
      severity: 'error',
      summary: 'Error Removing Association',
      detail: `Error removing association between ${props.email} (${props.fullName}) and ${props.entityName}).\n${e}`,
      life: TOAST_SAVE_LIFE
    });
  }
};

const onBan = async () => {
  try {
    const result = await setAdminCanLogin(props.userId, false);
    if (result) {
      toast.add({
        group: 'top-toast',
        severity: 'success',
        summary: 'Successfully Banned User',
        detail: `${props.email} has been banned.`,
        life: TOAST_SAVE_LIFE
      });
      emit('modified');
    } else {
      toast.add({
        group: 'top-toast',
        severity: 'error',
        summary: 'Error Banning User',
        detail: `Error banning ${props.email} (${props.fullName}).\nInvalid query result.`,
        life: TOAST_SAVE_LIFE
      });
    }
  } catch (e) {
    toast.add({
      group: 'top-toast',
      severity: 'error',
      summary: 'Error Banning User',
      detail: `Error banning ${props.email} (${props.fullName}).\n${e}`,
      life: TOAST_SAVE_LIFE
    });
  }
};

const onUnban = async () => {
  try {
    const result = await setAdminCanLogin(props.userId, true);
    if (result) {
      toast.add({
        group: 'top-toast',
        severity: 'success',
        summary: 'Successfully Unbanned User',
        detail: `${props.email} has been unbanned.`,
        life: TOAST_SAVE_LIFE
      });
      emit('modified');
    } else {
      toast.add({
        group: 'top-toast',
        severity: 'error',
        summary: 'Error Unbanning User',
        detail: `Error unbanning ${props.email} (${props.fullName}).\nInvalid query result.`,
        life: TOAST_SAVE_LIFE
      });
    }
  } catch (e) {
    toast.add({
      group: 'top-toast',
      severity: 'error',
      summary: 'Error Unbanning User',
      detail: `Error unbanning ${props.email} (${props.fullName}).\n${e}`,
      life: TOAST_SAVE_LIFE
    });
  }
};

const deleteLabel = computed(() => `remove ${props.email} (${props.fullName}) association with ${props.entityName}`);
const banLabel = computed(() => `block ${props.email} (${props.fullName})`);
const unbanLabel = computed(() => `unblock ${props.email} (${props.fullName})`);
</script>

<style scoped>
.button-group {
  display: inline-flex;
}
</style>